/**
 * Remove Co-Borrower Section
 *
 * Bottom-of-step "danger zone" card that lets the dealer remove the co-borrower
 * from the current DGA Draft application. Renders as the final FormTable in the
 * co-borrower step. The actual API call and post-success navigation are owned
 * by ApplicationModal — this component is purely presentational and only
 * manages local dialog open state.
 */

import * as React from "react";
import { Button } from "@porchpass/ui";
import { FormTable, FormTableBody } from "@porchpass/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@porchpass/ui";
import { UnavailableIcon } from "@porchpass/ui";

interface RemoveCoBorrowerSectionProps {
  /** Whether the delete request is currently in flight */
  isLoading: boolean;
  /** Whether the parent modal is still loading the application */
  isAppLoading: boolean;
  /** Triggered when the user confirms removal — fires the API call */
  onConfirm: () => void;
}

export const RemoveCoBorrowerSection: React.FC<
  RemoveCoBorrowerSectionProps
> = ({ isLoading, isAppLoading, onConfirm }) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const handleOpenChange = (open: boolean) => {
    // Block dismissal while the request is in flight to prevent the user
    // from thinking they cancelled when the request will still complete.
    if (isLoading) return;
    setConfirmOpen(open);
  };

  return (
    <FormTable
      title="Remove Co-Borrower"
      headerIcon={
        <UnavailableIcon
          variant="bulk-rounded"
          size={16}
          className="text-red-600"
        />
      }
    >
      <FormTableBody>
        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-on-surface-muted">
            Remove this co-borrower from the application. All information they
            entered will be permanently deleted.
          </p>
          <div>
            <Button
              variant="destructive-secondary"
              size="s"
              disabled={isAppLoading || isLoading || confirmOpen}
              onClick={() => setConfirmOpen(true)}
            >
              Remove Co-Borrower
            </Button>
          </div>
        </div>
      </FormTableBody>

      <Dialog open={confirmOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="flex gap-2 items-center">
              <UnavailableIcon size={18} className="text-red-600" />
              <DialogTitle>Remove Co-Borrower?</DialogTitle>
            </div>
          </DialogHeader>
          <DialogBody>
            <p className="text-base text-[#4a5466]">
              This will permanently remove the co-borrower and all their
              information from this application. This action cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              size="s"
              disabled={isLoading}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive-secondary"
              size="s"
              loading={isLoading}
              disabled={isLoading}
              onClick={onConfirm}
            >
              Yes, remove co-borrower
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FormTable>
  );
};
